import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IonicModule, PopoverController, AlertController, LoadingController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import moment from 'moment';

import { ActionDropdownComponent } from 'src/app/components/action-dropdown/action-dropdown.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { getWhereClausesFromFilters } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';

interface KnowledgeGapTopic {
  id: string;
  conversationId: string;
  topicTitle: string;
  topicDescription: string;
  context: string;
  confidence: number;
  createdAt: string;
}

interface KnowledgeGapInsight {
  id: string;
  category: string;
  language: string;
  topics: {
    id: string;
    topicTitle: string;
    conversationId: string;
    conversation: {
      id: string;
      title: string | null;
      createdAt: string;
    };
  }[];
}

interface KnowledgeGapRecommendation {
  // Fields from the database model
  id: string;
  tenantId: string;
  projectId: string | null;
  category: string;
  recommendationType: 'create' | 'update';
  existingArticleId: string | null;
  ignoredAt: string | null;
  ignoredBy: string | null;
  createdAt: string;
  updatedAt: string;

  // Fields added by the backend API
  existingArticleTitle: string | null;
  insight: KnowledgeGapInsight;

  // Only available in detail view
  topics?: KnowledgeGapTopic[];
}

interface KnowledgeGapRecommendationsResponse {
  recommendations: KnowledgeGapRecommendation[];
  cursors: {
    next?: string;
    previous?: string;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  totalCount?: number;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    SharedPipesModule,
    RouterModule,
    FilterButtonComponent,
  ],
  selector: 'app-knowledge-gap-recommendations',
  templateUrl: './knowledge-gap-recommendations.component.html',
  styleUrls: ['./knowledge-gap-recommendations.component.scss'],
})
export class KnowledgeGapRecommendationsComponent implements OnInit {
  isLoading = true;
  ColumnMode = ColumnMode;

  limit = 20;
  cursor: any;
  searchText?: string;
  recommendations: KnowledgeGapRecommendation[] = [];

  sortField = 'createdAt';
  sortDesc = true;
  sortLabel = 'Newest';

  filters: SelectedFilterMap = new Map();
  filterOptions: FilterOption[] = [
    {
      label: 'Recommendation Type',
      id: 'recommendationType',
      icon: 'construct',
      type: 'select',
      values: [
        {
          label: 'Create New Article',
          id: 'create',
        },
        {
          label: 'Update Existing Article',
          id: 'update',
        },
      ],
    },
    {
      label: 'Project',
      id: 'projectId',
      icon: 'folder-open',
      type: 'select',
    },
  ];

  fetchRecommendations = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest = {}): Promise<FetchDataResponse<KnowledgeGapRecommendation>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      search: this.searchText || undefined,
    };

    const where = this.getWhereClauses();
    if (where.length) {
      options.where = where;
    }

    // Add pagination parameters
    const params: any = {
      limit,
      descending: descending ? 'true' : 'false',
    };

    // Only add cursor if it exists
    if (cursor) {
      params.cursor = cursor;
    }

    // Add filter parameters
    if (this.authService.selectedProject) {
      params.projectId = this.authService.selectedProject.id;
    }

    // Add search text
    if (this.searchText) {
      params.search = this.searchText;
    }

    // Process filters - convert from Map to backend parameters
    this.filters.forEach((values, filterOption) => {
      if (values && values.length > 0) {
        const selectedValue = values[0].id;

        switch (filterOption.id) {
          case 'recommendationType':
            params.recommendationType = selectedValue;
            break;
          case 'projectId':
            params.projectId = selectedValue;
            break;
        }
      }
    });
    const result = (await this.apiService.getPromise(
      `/tenants/${this.authService.tenantId}/knowledge-gaps/recommendations`,
      params,
    )) as KnowledgeGapRecommendationsResponse;

    return {
      data: result.recommendations,
      totalCount: result.totalCount || 0,
      cursors: result.cursors,
    };
  };

  recommendationsDataSource = createDataSource(this.fetchRecommendations);

  constructor(
    public authService: AuthService,
    private apiService: ApiService,
    private popoverCtrl: PopoverController,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.getRecommendationsInitial();
  }

  async getRecommendationsInitial() {
    this.isLoading = true;
    try {
      await this.recommendationsDataSource.firstPage();
      this.updateFilterOptions();
    } finally {
      this.isLoading = false;
    }
  }

  getWhereClauses() {
    const filterWheres = getWhereClausesFromFilters(this.filters);

    if (this.authService.selectedProject) {
      filterWheres.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    return filterWheres;
  }

  async getRecommendations() {
    this.isLoading = true;
    try {
      await this.recommendationsDataSource.firstPage();
      this.updateFilterOptions();
    } finally {
      this.isLoading = false;
    }
  }

  updateFilterOptions() {
    // Project filter options could be populated from authService.projects if needed
    // For now, keeping it simple since users can already filter by selected project
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.getRecommendations();
  }

  displayDate(date: string) {
    return moment(date).fromNow();
  }

  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  }

  getRecommendationTypeIcon(type: string): string {
    return type === 'create' ? 'add-circle' : 'create';
  }

  getRecommendationTypeColor(type: string): string {
    return type === 'create' ? 'primary' : 'secondary';
  }

  async showRecommendationActions(recommendation: KnowledgeGapRecommendation, event: Event) {
    const isIgnored = !!recommendation.ignoredAt;
    const options = [
      {
        label: isIgnored ? 'Unignore' : 'Ignore',
        icon: isIgnored ? 'eye' : 'eye-off',
      },
      {
        label: 'View Details',
        icon: 'information-circle',
      },
    ];

    if (recommendation.recommendationType === 'create') {
      options.unshift({
        label: 'Create Article',
        icon: 'add-circle',
      });
    } else if (recommendation.existingArticleId) {
      options.unshift({
        label: 'Edit Article',
        icon: 'create',
      });
    }

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number, label: string) => {
          popover.dismiss();
          await this.handleRecommendationAction(recommendation, index, label);
        },
      },
      showBackdrop: false,
      event,
    });

    await popover.present();
  }

  private async handleRecommendationAction(recommendation: KnowledgeGapRecommendation, index: number, label: string) {
    const isIgnored = !!recommendation.ignoredAt;
    const actionMap: { [key: string]: () => Promise<void> } = {};

    if (recommendation.recommendationType === 'create') {
      actionMap['Create Article'] = () => this.createArticleFromRecommendation(recommendation);
      actionMap[isIgnored ? 'Unignore' : 'Ignore'] = () => this.toggleIgnoreRecommendation(recommendation);
      actionMap['View Details'] = () => this.viewRecommendationDetails(recommendation);
    } else {
      if (recommendation.existingArticleId) {
        actionMap['Edit Article'] = () => this.editExistingArticle(recommendation);
      }
      actionMap[isIgnored ? 'Unignore' : 'Ignore'] = () => this.toggleIgnoreRecommendation(recommendation);
      actionMap['View Details'] = () => this.viewRecommendationDetails(recommendation);
    }

    const selectedAction = Object.keys(actionMap)[index];
    if (selectedAction && actionMap[selectedAction]) {
      await actionMap[selectedAction]();
    }
  }

  async createArticleFromRecommendation(recommendation: KnowledgeGapRecommendation) {
    this.router.navigate(['../articles/new'], {
      relativeTo: this.route,
      queryParams: {
        recommendationId: recommendation.id,
        from: 'knowledge-gaps',
      },
    });
  }

  async editExistingArticle(recommendation: KnowledgeGapRecommendation) {
    if (recommendation.existingArticleId) {
      this.router.navigate([`../articles/${recommendation.existingArticleId}/edit`], {
        relativeTo: this.route,
        queryParams: {
          recommendationId: recommendation.id,
          from: 'knowledge-gaps',
        },
      });
    }
  }

  async toggleIgnoreRecommendation(recommendation: KnowledgeGapRecommendation) {
    const isIgnored = !!recommendation.ignoredAt;
    const loading = await this.loadingController.create({
      message: `${isIgnored ? 'Unignoring' : 'Ignoring'} recommendation...`,
    });
    await loading.present();

    try {
      await this.apiService.putPromise(
        `/tenants/${this.authService.tenantId}/knowledge-gaps/recommendations/${recommendation.id}`,
        { ignore: !isIgnored },
      );

      await loading.dismiss();
      this.messageService.show(`Recommendation ${isIgnored ? 'unignored' : 'ignored'} successfully.`);
      await this.recommendationsDataSource.reloadPage();
    } catch (error) {
      await loading.dismiss();
      this.messageService.showError(
        `Failed to ${isIgnored ? 'unignore' : 'ignore'} recommendation: ` + (error.message || 'Unknown error'),
      );
    }
  }

  async viewRecommendationDetails(recommendation: KnowledgeGapRecommendation) {
    // Navigate to a details view or show a modal
    this.router.navigate([`./recommendations/${recommendation.id}`], {
      relativeTo: this.route,
    });
  }

  getRelatedTopicsDisplay(topics: string[]): string {
    if (!topics || topics.length === 0) return '';
    if (topics.length <= 2) return topics.join(', ');
    return `${topics.slice(0, 2).join(', ')} +${topics.length - 2} more`;
  }

  getSynopsis(recommendation: KnowledgeGapRecommendation): string {
    if (!recommendation.insight || !recommendation.insight.topics || recommendation.insight.topics.length === 0) {
      return '';
    }

    const totalTopics = this.getTopicsFromInsights(recommendation).length;
    const totalConversations = this.getTotalConversationCount(recommendation);

    if (recommendation.recommendationType === 'create') {
      return `Analysis of ${totalConversations} conversations identified ${totalTopics} recurring topics that need documentation in ${recommendation.category}.`;
    } else {
      return `${totalTopics} topics from ${totalConversations} conversations suggest updates needed for existing ${recommendation.category} documentation.`;
    }
  }

  getConversationUrl(topic: string): string {
    // Simple implementation - navigate to conversations with a search for the topic
    const encodedTopic = encodeURIComponent(topic);
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations?search=${encodedTopic}`;
  }

  getConversationUrlByTopic(topic: KnowledgeGapTopic): string {
    // Navigate directly to the conversation using conversationId
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations/${topic.conversationId}`;
  }

  getConversationUrlForCategory(category: string): string {
    // Navigate to conversations page with search for the category
    const encodedCategory = encodeURIComponent(category);
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations?search=${encodedCategory}`;
  }

  getTotalConversationCount(recommendation: KnowledgeGapRecommendation): number {
    return recommendation.insight?.topics?.length || 0;
  }

  getTopicsFromInsights(recommendation: KnowledgeGapRecommendation) {
    return recommendation.insight?.topics || [];
  }

  getConversationUrlForTopic(topic: { conversationId: string }): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations/${topic.conversationId}`;
  }

  async viewConversationsForRecommendation(recommendation: KnowledgeGapRecommendation, event: Event) {
    event.preventDefault();

    // Navigate to recommendation detail which should have all conversation links
    this.router.navigate([`./recommendations/${recommendation.id}`], {
      relativeTo: this.route,
    });
  }

  trackByRecommendation(index: number, recommendation: KnowledgeGapRecommendation): string {
    return recommendation.id;
  }

  async nextPage() {
    this.isLoading = true;
    try {
      await this.recommendationsDataSource.nextPage();
    } finally {
      this.isLoading = false;
    }
  }

  async previousPage() {
    this.isLoading = true;
    try {
      await this.recommendationsDataSource.previousPage();
    } finally {
      this.isLoading = false;
    }
  }
}
